import { qoursesApi } from '@/api/qourses.tsx'
import { CourseName } from '@/components/Resolvers.tsx'
import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import useGetCourse, { getCourseQueryKey } from '@/hooks/courses/useGetCourse.tsx'
import { getCoursesQueryKey } from '@/hooks/courses/useGetCourses.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog.tsx'
import { ToastVariant, minDelay, sendGenericErrorNotification, sendNotification } from '@/utils.tsx'
import { useQueryClient } from '@tanstack/react-query'
import { Archive, Calendar, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function ArchiveCourseModal({ courseId }: { courseId: string }) {
  const { course, isLoading, isError } = useGetCourse(courseId)
  const [isDeleting, setIsDeleting] = useState(false)

  const queryClient = useQueryClient()

  const { t: translate } = useTranslation()

  const handleArchiveCourse = async () => {
    try {
      setIsDeleting(true)
      await minDelay(qoursesApi.course.courseControllerArchiveCourseV1(course.id), 1000)
      sendNotification(
        translate('modals.archiveCourseModal.notification.title'),
        translate('modals.archiveCourseModal.notification.subtitle'),
        ToastVariant.Success,
      )
      popAllModals()
      queryClient.invalidateQueries(getCoursesQueryKey({}))
      queryClient.invalidateQueries(getCourseQueryKey(courseId))
    } catch (e) {
      console.error(e)
      sendGenericErrorNotification()
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('common.loading')}</DialogTitle>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  if (isError) {
    return (
      <Dynamic.Content>
        <DialogHeader>
          <DialogTitle>{translate('modals.archiveCourseModal.errorState.title')}</DialogTitle>
          <DialogDescription>
            {translate('modals.archiveCourseModal.errorState.subtitle')}
          </DialogDescription>
        </DialogHeader>
      </Dynamic.Content>
    )
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogHeader className="mb-2">
          <DialogTitle className="mt-6 sm:mt-2"></DialogTitle>
          <DialogTitle className="mt-6 flex flex-col gap-y-2 sm:mt-2">
            <div className="flex flex-row items-center justify-center text-sm text-muted-foreground sm:justify-start">
              <Calendar className="mr-1 size-4 text-muted-foreground" />
              <CourseName courseId={courseId} />
            </div>
            <p>{translate('modals.archiveCourseModal.title')}</p>
          </DialogTitle>
          <DialogDescription>{translate('modals.archiveCourseModal.subtitle')}</DialogDescription>
        </DialogHeader>
      </DialogHeader>
      <Button
        variant="destructive"
        className="bezel"
        disabled={isDeleting}
        onClick={handleArchiveCourse}
      >
        {isDeleting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {translate('common.loading')}
          </>
        ) : (
          <>
            <Archive className="mr-2 h-4 w-4" />
            {translate('modals.archiveCourseModal.button')}
          </>
        )}
      </Button>
    </Dynamic.Content>
  )
}
